import { useEffect } from 'react'
import { SearchQuery, SearchTemplate, SearchView } from '../../../../generic-search-common/src'
import { useState } from 'react'
import { CommonAdvancedSearchProps, getSearchTemplateWithDisplayFilters, useSearch } from '../../hooks/search'
import { Filters } from '../filters'
import { Results } from '../results'
import { useGetSearchConfig } from '../../hooks/use-api'
import { ExportConfigProps } from '../results/component'
import { useNavigate } from 'react-router-dom'
import {
  ConditionalCardWrapper,
  Heading,
  InlineNotification,
  LoadingIndicator,
  TabbedContent,
  TableSortByProps,
} from '@msaf/core-react'
import { DISPLAY_DATE_FORMAT } from '@msaf/core-common'

export interface AdvancedSearchTableProps extends CommonAdvancedSearchProps {
  reinflateQuery?: SearchQuery
  customActions?: { [key: string]: (...args: any[]) => void }
  currentUserPermissions?: Array<string>
  isShowResultsOnMap?: boolean
  isTabbedContent?: boolean
  noResultsComponent?: JSX.Element
  defaultSortBy: TableSortByProps
  resultsHeading?: string
  isShowResultsCard?: boolean
  isShowFiltersCard?: boolean
  exportConfig?: Array<ExportConfigProps>
  resultNameSingular?: string
  resultNamePlural?: string
  showResultsCount?: boolean
}

export function AdvancedSearchTable({
  requestOptions,
  searchTypeKey,
  customActions,
  dateFormat,
  searchTemplate,
  onSearchQueryChange,
  pageSize,
  currentUserPermissions,
  isShowResultsOnMap,
  isTabbedContent = true,
  filterBy,
  noResultsComponent: NoResultsComponent,
  defaultSortBy,
  resultsHeading,
  isShowResultsCard = true,
  isShowFiltersCard = true,
  viewKey,
  exportConfig,
  resultNameSingular,
  resultNamePlural,
  showResultsCount,
  cachedSearchQuery,
}: AdvancedSearchTableProps) {
  const navigate = useNavigate()
  const [activeSearchTemplate, setActiveSearchTemplate] = useState<SearchTemplate | undefined>()

  useEffect(() => {
    if (searchTemplate) {
      // Filter out the hidden filters so that the rest of the logic need not worry about it
      const template = getSearchTemplateWithDisplayFilters(searchTemplate)
      setActiveSearchTemplate(template)
    }
  }, [searchTemplate])

  const { isSuccess, data } = useGetSearchConfig(
    searchTypeKey,
    requestOptions,
    !!searchTypeKey && !activeSearchTemplate,
  )

  useEffect(() => {
    if (isSuccess && data?.searchTemplate) {
      // Filter out the hidden filters so that the rest of the logic need not worry about it
      const searchTemplate: SearchTemplate = data?.searchTemplate
      const template = getSearchTemplateWithDisplayFilters(searchTemplate)
      setActiveSearchTemplate(template)
    }
  }, [isSuccess, data?.searchTemplate])

  const { searchQuery, searchResults, currentView, updateQuery, setCurrentPage, setSortColumn } = useSearch({
    searchTemplate: activeSearchTemplate,
    pageSize,
    requestOptions,
    onSearchQueryChange,
    filterBy,
    viewKey,
    cachedSearchQuery,
  })

  const switchToMapView = () => {
    activeSearchTemplate?.mapConfig?.mapViewEmberRoute && navigate(activeSearchTemplate?.mapConfig?.mapViewEmberRoute)
  }

  // Check if the search template has been loaded and the search results have been updated
  if (
    !(activeSearchTemplate && searchQuery && currentView) ||
    (searchQuery?.viewKey && searchResults?.viewKey !== searchQuery?.viewKey)
  ) {
    return <LoadingIndicator>Loading&hellip;</LoadingIndicator>
  }

  const DisplayFiltersComponent = (
    <>
      <Heading level={3} className='generic-search-filter__filters-label'>
        {activeSearchTemplate.searchLabel ? activeSearchTemplate.searchLabel : 'Search criteria'}
        {!activeSearchTemplate.savedSearchDisabled && requestOptions?.authStrategy !== 'none' && (
          <button className='c-secondary-action' onClick={() => {}}>
            Saved searches
          </button>
        )}
      </Heading>
      <Filters
        searchTemplate={activeSearchTemplate}
        searchTypeKey={searchTypeKey}
        onSearch={updateQuery}
        requestOptions={requestOptions}
        dateFormat={dateFormat ?? DISPLAY_DATE_FORMAT}
        searchQuery={searchQuery}
        isMapMode={false}
        switchToMapView={switchToMapView}
      />
    </>
  )

  const DisplayNoResultsComponent = NoResultsComponent ? (
    NoResultsComponent
  ) : (
    <InlineNotification messageType='info' isDismissable={false}>
      No results available
    </InlineNotification>
  )

  return (
    <div>
      {/* === Search filters === */}
      {!activeSearchTemplate.searchFiltersDisabled && (
        <ConditionalCardWrapper condition={isShowFiltersCard} wrapper={(children) => children}>
          {isTabbedContent ? (
            <TabbedContent
              includeHorizontalPadding={true}
              ariaLabel='Search filters'
              tabs={activeSearchTemplate.views
                .filter((view: SearchView) => !(view.isMapView || view.isMapDetailView || view.isExportOnlyView))
                .map((view: SearchView) => {
                  return {
                    label: view.viewName,
                    tabContent: DisplayFiltersComponent,
                  }
                })}
            />
          ) : (
            <div className='generic-search-filter__filters-container--padded'>{DisplayFiltersComponent}</div>
          )}
        </ConditionalCardWrapper>
      )}

      {searchResults && !!searchResults.totalRows ? (
        <Results
          currentView={currentView}
          searchResults={searchResults}
          searchQuery={searchQuery}
          setCurrentPage={setCurrentPage}
          setSortColumn={setSortColumn}
          customActions={customActions}
          searchTemplate={activeSearchTemplate}
          currentUserPermissions={currentUserPermissions}
          isShowResultsOnMap={isShowResultsOnMap}
          defaultSortBy={defaultSortBy}
          heading={resultsHeading}
          isShowResultsCard={isShowResultsCard}
          exportConfig={exportConfig}
          resultNameSingular={resultNameSingular}
          resultNamePlural={resultNamePlural}
          showResultsCount={showResultsCount}
          pageSize={pageSize}
          dateFormat={dateFormat}
        />
      ) : (
        DisplayNoResultsComponent
      )}
    </div>
  )
}
